import {
  type CellAddress,
  useBoardColumnPaging,
  useBoardItemPaging,
  useBoardLabels,
  useBoardRowPaging,
} from '@github-ui/board-core'
import {clsx} from 'clsx'

import {useAnnounceOnce} from './announce-once'
import styles from './BoardPaging.module.css'
import {BoardColumnSkeleton, BoardItemsLoadingRow, BoardRowSkeleton} from './BoardSkeletons'

/**
 * The three paging directions, as three components that never conflate them.
 *
 * There is no control to press. Each of these draws a ghost at the end of what is loaded
 * when, and only when, that direction says it has more, and reaching the ghost is what
 * asks for the next page. Nothing in here fetches: it reports, and the next page arrives
 * as new `data`.
 *
 * A ghost is not a loading skeleton. A skeleton shimmers because something is happening;
 * a ghost is still, because nothing is, and it is only saying that something else
 * exists. Using the skeleton for both would leave a board that is doing nothing looking
 * permanently busy, and put one unstoppable animation per cell on screen.
 *
 * An axis ghost says so in words rather than in a shape, and stands in the space the
 * column or swimlane that has not arrived yet will occupy, so its outline describes a
 * place rather than offering an action.
 *
 * A cell's ghost says the same thing and is not drawn at all. There is one at the end of
 * every cell that has more, which on a paged board is most of them, and drawn they are a
 * line of repeated text down every column saying only that the board is not finished. It
 * is still in the page, because it is the observer's target, the board's focus stop for
 * that cell, and the only way a screen reader is told the cell has more; it is clipped
 * rather than removed, and it comes back when focus lands on it. See
 * `BoardPaging.module.css`.
 */

export type BoardItemPagingProps = {
  address: CellAddress
  className?: string
}

/**
 * More cards in one cell: the sentinel, and the wording that says the cell has more.
 *
 * Renders nothing at all in a cell with nothing more to load, so such a cell mounts no
 * sentinel and costs no observer. Everything it reads is addressed to this cell, so a
 * neighbour paging never puts this one into a loading state.
 *
 * **It holds its room open whether or not a page is in flight**, which is what lets the
 * loading row appear without moving anybody. A page is asked for while there is still a
 * little left to scroll through, so a row that grew the cell when it arrived would push
 * itself below the fold at the very moment it had something to say: 5 visible pixels of
 * 56, measured, before the room was held. Reserved, the swap costs nothing and the row is
 * already on screen for a reader who has reached the end.
 */
export function BoardItemPaging({address, className}: BoardItemPagingProps) {
  const {hasMore, isLoading, label, sentinelProps} = useBoardItemPaging(address)

  // A cell can be loading its first page while stating it has no more, because only the
  // server knows yet. It still says so; it just has nothing to observe.
  if (!hasMore && !isLoading) return null

  return (
    <div {...sentinelProps} className={clsx(styles.ItemPaging, className)}>
      {isLoading ? <BoardItemsLoadingRow /> : <p className={clsx(styles.Ghost, styles.CardGhost)}>{label}</p>}
    </div>
  )
}

export type BoardColumnPagingProps = {
  className?: string
}

/**
 * More columns, or the notice that there will not be any.
 *
 * The two are opposite advice and never appear together: a ghost says keep scrolling,
 * and the notice says stop and narrow the search. Which one shows is settled in
 * `useBoardColumnPaging`, so a caller that states both still gets one.
 */
export function BoardColumnPaging({className}: BoardColumnPagingProps) {
  const {hasMore, isLoading, isTruncated, label, truncationLabel, sentinelProps} = useBoardColumnPaging()
  const labels = useBoardLabels()

  useAnnounceOnce(labels.loadingColumns, isLoading)
  // The advice most worth hearing, and the one nobody asked for: an axis stops being
  // enumerated because of what the reader searched for, and the notice arrives with new
  // data rather than with a keystroke.
  useAnnounceOnce(truncationLabel, isTruncated)

  if (isTruncated) {
    return <p className={clsx(styles.Truncated, styles.ColumnsTruncated, className)}>{truncationLabel}</p>
  }

  if (!hasMore && !isLoading) return null

  return (
    <div {...sentinelProps} className={clsx(styles.ColumnPaging, className)}>
      {isLoading ? <BoardColumnSkeleton /> : <p className={clsx(styles.Ghost, styles.ColumnGhost)}>{label}</p>}
    </div>
  )
}

export type BoardRowPagingProps = {
  className?: string
}

/**
 * More rows, or the notice that there will not be any.
 *
 * A board with no swimlane runs this too. It states `hasMoreRows: false`, which is the
 * ordinary way for an axis to say it is complete, so there is no second path here for
 * the unswimlaned case.
 */
export function BoardRowPaging({className}: BoardRowPagingProps) {
  const {hasMore, isLoading, isTruncated, label, truncationLabel, sentinelProps} = useBoardRowPaging()
  const labels = useBoardLabels()

  useAnnounceOnce(labels.loadingRows, isLoading)
  useAnnounceOnce(truncationLabel, isTruncated)

  if (isTruncated) {
    return <p className={clsx(styles.Truncated, styles.RowsTruncated, className)}>{truncationLabel}</p>
  }

  if (!hasMore && !isLoading) return null

  return (
    <div {...sentinelProps} className={clsx(styles.RowPaging, className)}>
      {isLoading ? <BoardRowSkeleton /> : <p className={clsx(styles.Ghost, styles.RowGhost)}>{label}</p>}
    </div>
  )
}
